'use client';

import { useState, useEffect } from 'react';
import type { Anniversary } from '@/lib/types';
import { calculateAge, getNextBirthday } from '@/lib/date-utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PartyPopper } from 'lucide-react';

interface AnniversaryCountdownProps {
  anniversary: Anniversary;
}

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export function AnniversaryCountdown({ anniversary }: AnniversaryCountdownProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  const nextAnniversaryDate = getNextBirthday(anniversary.date);
  const celebratingYears = (calculateAge(anniversary.date) ?? 0) + 1;

  useEffect(() => {
    if (!nextAnniversaryDate) return;

    const timer = setInterval(() => {
      const now = new Date();
      const distance = nextAnniversaryDate.getTime() - now.getTime();

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [nextAnniversaryDate]);

  if (!timeLeft) {
    return null;
  }

  return (
    <Card className="text-center">
      <CardHeader>
        <CardDescription className="flex items-center justify-center gap-2">
            <PartyPopper className="h-4 w-4 text-muted-foreground" />
            Next Anniversary
        </CardDescription>
        <CardTitle className="text-3xl">
          {anniversary.name} - Celebrating {celebratingYears} years!
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap justify-center gap-5 text-center">
            <div className="flex flex-col p-4 bg-secondary rounded-lg text-secondary-foreground min-w-[80px]">
                <span className="font-mono text-5xl tabular-nums">
                <span>{String(timeLeft.days).padStart(2, '0')}</span>
                </span>
                days
            </div>
            <div className="flex flex-col p-4 bg-secondary rounded-lg text-secondary-foreground min-w-[80px]">
                <span className="font-mono text-5xl tabular-nums">
                <span>{String(timeLeft.hours).padStart(2, '0')}</span>
                </span>
                hours
            </div>
            <div className="flex flex-col p-4 bg-secondary rounded-lg text-secondary-foreground min-w-[80px]">
                <span className="font-mono text-5xl tabular-nums">
                <span>{String(timeLeft.minutes).padStart(2, '0')}</span>
                </span>
                min
            </div>
            <div className="flex flex-col p-4 bg-secondary rounded-lg text-secondary-foreground min-w-[80px]">
                <span className="font-mono text-5xl tabular-nums">
                <span>{String(timeLeft.seconds).padStart(2, '0')}</span>
                </span>
                sec
            </div>
        </div>
      </CardContent>
    </Card>
  );
}
